import { useCallback, useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import api from "../../api/api";
import { Alert, Field, Panel, Pill, apiError, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

/** Coaches and org admins. Coaches must already have a Build My Biz Brand coach account. */
export default function OrgStaff() {
    const { orgId, reloadOrg } = useOrgPortal();
    const s = useOrgStyles();
    const [staff, setStaff] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ email: "", isCoach: true, isOrgAdmin: false });

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/staff`);
            setStaff(res.data.staff || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load staff."));
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const act = async (fn, success) => {
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const res = await fn();
            if (res?.data?.staff) setStaff(res.data.staff); else await load();
            await reloadOrg();
            if (success) setNotice(success);
            return true;
        } catch (err) {
            setError(apiError(err));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const add = async (e) => {
        e.preventDefault();
        const ok = await act(() => api.post(`/org/${orgId}/staff`, form),
            form.isCoach ? "Coach added. Assign them a coach license so they can see your students." : "Added.");
        if (ok) setForm({ email: "", isCoach: true, isOrgAdmin: false });
    };

    const updateRoles = (m, roles) => act(() => api.put(`/org/${orgId}/staff/${m.OrgStaffID}`, {
        isCoach: !!m.IsCoach, isOrgAdmin: !!m.IsOrgAdmin, ...roles,
    }));

    const remove = (m) => {
        if (!window.confirm(`Remove ${m.FirstName} ${m.LastName} from your organization? Any coach license they hold is removed.`)) return;
        act(() => api.delete(`/org/${orgId}/staff/${m.OrgStaffID}`));
    };

    return (
        <div className="grid gap-6 lg:grid-cols-3">
            <Panel title="Coaches & admins" padded={false} className="lg:col-span-2">
                <div className="px-5 pt-4">
                    <Alert onClose={() => setError("")}>{error}</Alert>
                    <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
                </div>
                {!staff ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : (
                    <ul className={`divide-y ${s.divider}`}>
                        {staff.map((m) => (
                            <li key={m.OrgStaffID} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                    <div className={`font-semibold ${s.text}`}>{m.FirstName} {m.LastName}</div>
                                    <div className={`truncate text-xs ${s.muted}`}>{m.Email}</div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {m.IsOrgAdmin ? <Pill tone="primary">Org admin</Pill> : null}
                                        {m.IsCoach ? <Pill>Coach</Pill> : null}
                                        {m.IsCoach ? (m.HasCoachLicense ? <Pill tone="success">Licensed</Pill> : <Pill tone="warning">No license</Pill>) : null}
                                        {!m.AccountActive && <Pill tone="warning">Account pending</Pill>}
                                    </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-sm">
                                    {m.AdminTypeID === 6 && (
                                        <label className={`inline-flex items-center gap-1.5 ${s.body}`}>
                                            <input type="checkbox" checked={!!m.IsOrgAdmin} disabled={busy}
                                                   onChange={(e) => updateRoles(m, { isOrgAdmin: e.target.checked })} />
                                            Org admin
                                        </label>
                                    )}
                                    <button type="button" className={s.btnDanger} disabled={busy} onClick={() => remove(m)}>Remove</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Panel>

            <Panel title="Add a coach or admin">
                <form onSubmit={add} className="space-y-4">
                    <Field label="Email" htmlFor="st-email" hint="They need a Build My Biz Brand coach account first. Ask them to sign up as a coach, then add them here.">
                        <input id="st-email" type="email" required className={s.input} value={form.email}
                               onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                    </Field>
                    <fieldset className="space-y-2">
                        <legend className={`mb-1 text-sm font-medium ${s.body}`}>Role</legend>
                        <label className={`flex items-center gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" checked={form.isCoach} onChange={(e) => setForm((f) => ({ ...f, isCoach: e.target.checked }))} />
                            Coach (sees your students once licensed)
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" checked={form.isOrgAdmin} onChange={(e) => setForm((f) => ({ ...f, isOrgAdmin: e.target.checked }))} />
                            Org admin (manages programs, students, licenses)
                        </label>
                    </fieldset>
                    <button type="submit" className={`${s.btnPrimary} w-full`} disabled={busy || !form.email || (!form.isCoach && !form.isOrgAdmin)}>
                        <UserPlus size={16} /> Add
                    </button>
                </form>
            </Panel>
        </div>
    );
}
